import { API_URL } from './demoMode';

function normalizeBase(base) {
  if (!base) return '';
  return base.endsWith('/') ? base.slice(0, -1) : base;
}

function normalizePath(path) {
  if (!path) return '/';
  return path.startsWith('/') ? path : `/${path}`;
}

function toUnique(list) {
  return [...new Set(list)];
}

function getCandidateBases() {
  const primary = normalizeBase(API_URL);
  const candidates = [primary];

  // Fallback for deployments where /api proxy is missing and backend is mounted at root.
  if (primary === '/api') {
    candidates.push('');
  }

  return toUnique(candidates);
}

async function parseResponse(response) {
  const contentType = response.headers.get('content-type') || '';
  const rawText = await response.text();
  const looksJson = contentType.includes('application/json') || /^\s*[[{]/.test(rawText);

  if (!looksJson) {
    return { payload: null, rawText };
  }

  try {
    return { payload: JSON.parse(rawText), rawText };
  } catch {
    return { payload: null, rawText };
  }
}

export async function apiGetJson(path, { token } = {}) {
  const safePath = normalizePath(path);
  const attempts = [];

  for (const base of getCandidateBases()) {
    const url = `${base}${safePath}`;

    try {
      const response = await fetch(url, {
        headers: token
          ? {
              Authorization: `Bearer ${token}`,
            }
          : undefined,
      });

      const { payload, rawText } = await parseResponse(response);
      const result = {
        ok: response.ok,
        status: response.status,
        payload,
        rawText,
        url,
      };

      attempts.push(result);

      // If auth failed, surface immediately to avoid masking with fallback retries.
      if (response.status === 401 || response.status === 403) {
        return { ...result, attempts };
      }

      if (response.ok) {
        return { ...result, attempts };
      }
    } catch (error) {
      attempts.push({
        ok: false,
        status: 0,
        payload: null,
        rawText: '',
        url,
        networkError: error instanceof Error ? error.message : 'Network error',
      });
    }
  }

  const last = attempts[attempts.length - 1] || {
    ok: false,
    status: 0,
    payload: null,
    rawText: '',
    url: '',
  };

  return {
    ...last,
    attempts,
  };
}

export function extractArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];

  const candidates = [
    payload.data,
    payload.items,
    payload.results,
    payload.users,
    payload.rows,
    payload.conversations,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate;
    }
  }

  return [];
}
